import { useState } from 'react';
import { BatchGenerateVideosRequest, VideoExportJobRequest, VideoExportJobResponse, VideoExportStatusResponse } from '@packages/global';
import { httpClient } from '../services/httpClient';

export function useBatchVideoGeneration() {
  const [loading, setLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState<{
    message: string;
    progress: number;
  } | null>(null);

  const batchGenerateVideos = async (episodeId: string, params: BatchGenerateVideosRequest) => {
    setLoading(true);
    try {
      const response = await httpClient.post(`/user/episodes/${episodeId}/batch-generate-videos`, params);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const exportVideo = async (episodeId: string, params: Omit<VideoExportJobRequest, 'episodeId'>): Promise<VideoExportJobResponse> => {
    setLoading(true);
    setExportProgress({ message: '提交导出任务...', progress: 0 });

    try {
      const response = await httpClient.post<VideoExportJobResponse>(`/user/videoExport/${episodeId}/export`, params);
      return response;
    } finally {
      setLoading(false);
    }
  };

  const getExportStatus = async (episodeId: string, jobId: string): Promise<VideoExportStatusResponse> => {
    const response = await httpClient.get<VideoExportStatusResponse>(`/user/videoExport/${episodeId}/export/${jobId}/status`);
    return response;
  };

  const pollExportStatus = async (episodeId: string, jobId: string, onProgress?: (status: VideoExportStatusResponse) => void): Promise<VideoExportStatusResponse> => {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const status = await getExportStatus(episodeId, jobId);

          if (onProgress) {
            onProgress(status);
          }

          setExportProgress({
            message: getProgressMessage(status.progress),
            progress: status.progress
          });

          if (status.status === 'completed') {
            setExportProgress(null);
            resolve(status);
          } else if (status.status === 'failed') {
            setExportProgress(null);
            reject(new Error(status.error || '导出失败'));
          } else {
            setTimeout(poll, 2000); // 2秒轮询一次
          }
        } catch (error) {
          setExportProgress(null);
          reject(error);
        }
      };

      poll();
    });
  };

  const getBatchVideoStatus = async (episodeId: string) => {
    const response = await httpClient.get(`/user/episodes/${episodeId}/batch-video-generation-status`);
    return response;
  };

  return {
    batchGenerateVideos,
    exportVideo,
    getExportStatus,
    pollExportStatus,
    getBatchVideoStatus,
    loading,
    exportProgress
  };
}

function getProgressMessage(progress: number): string {
  if (progress < 10) return '初始化导出任务...';
  if (progress < 20) return '构建时间轴配置...';
  if (progress < 25) return '提交云端剪辑任务...';
  if (progress < 95) return '云端视频合成中...';
  return '导出完成';
}